import { createFileRoute } from "@tanstack/react-router";
import {
  CreateBusinessFormSchema,
  types,
} from "@/components/business/business-meta";
import { Button } from "@/components/ui/button";
import { ImageField, InputField, SelectField } from "@/components/ui/form";
import { FileField } from "@/components/ui/form/file-field";
import {
  createFormData,
  useSimpleForm,
  validateObject,
} from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { State, Country } from "country-state-city";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import SetupLayout from "@/components/ui/layout/setup-layout";
import { useTranslate } from "@/hooks/use-translate";

export const Route = createFileRoute("/(onboarding)/setup/create-business")({
  component: AccountSetupPage,
});

function AccountSetupPage() {
  const store = useAppStore();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const { t } = useTranslate();

  const form = useSimpleForm({
    name: "",
    type: "",
    number_of_employees: "",
    tax_id_number: "",
    country: "",
    region: "",
    city: "",
    street: "",
    taxation_document: { file: undefined, url: "" },
    id_card_front: { file: undefined, src: "" },
    id_card_back: { file: undefined, src: "" },
  });

  useEffect(() => {
    store.auth.fetchSession();
    form.setValue("country", "CM");
    form.setValue("region", State.getStatesOfCountry("CM")[0]?.name);
    form.setValue("type", types[0]);
  }, []);

  const submit = async () => {
    store.errors.reset();
    store.loading.start("create");

    try {
      await store.business.create(
        createFormData({
          ...form.values,
          owner_id: store.auth.session?.user.id,
        })
      );

      store.loading.stop("create");
      navigate({ to: "/" });
    } catch (error) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      store.errors.catch(error);
      store.loading.stop("create");
    }
  };

  const nextStep = () => {
    // If the step required validation. Write the validation here.
    store.errors.reset();
    const schema = CreateBusinessFormSchema[step - 1];
    if (schema) {
      const validation = validateObject(form.values, schema);
      if (!validation.valid) {
        store.errors.setMany(validation.errors);
        return;
      }
    }
    setStep((prev) => Math.min(prev + 1, 3));
  };
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  return (
    <SetupLayout
      image="/assets/setup-1.svg"
      title={t("Setup your business")}
      description={t(
        "Provide the essential information about your business so we can helpyou manage it efficiently."
      )}
    >
      <div className="h-screen">
        <div className="flex items-center p-4 md:p-0  justify-center w-full h-full">
          <div className="w-full md:w-[500px]">
            {/* Step 1: Business Information */}

            <div className="flex items-center gap-2 mb-8">
              {[1, 2, 3].map((item, index: number) => (
                <div
                  key={`stepindix${index}`}
                  className={cn("rounded-md h-2 bg-gray-200 w-1/3", {
                    "bg-primary-400": step === item,
                  })}
                ></div>
              ))}
            </div>
            <div>{store.errors.render()}</div>

            {step === 1 && (
              <>
                <h3 className="font-semibold text-xl mt-4">
                  {t("Your business informations")}
                </h3>

                <div className="grid grid-cols-1 gap-4 mt-6">
                  <InputField
                    label={t("Full Business Name")}
                    type="text"
                    name="name"
                    value={form.values.name}
                    onChange={form.handleChange}
                    error={store.errors.values.name}
                    placeholder="e.g., SalesBox Retail Ltd."
                  />
                  <SelectField
                    label={t("Business Type")}
                    name="type"
                    value={form.values.type}
                    onChange={form.handleChange}
                    options={types.map((item: string) => ({
                      label: item,
                      value: item,
                    }))}
                    error={store.errors.values.type}
                    required
                  />
                  <InputField
                    type="number"
                    label={t("Number of Employees")}
                    name="number_of_employees"
                    value={form.values.number_of_employees}
                    onChange={form.handleChange}
                    error={store.errors.values.number_of_employees}
                  />
                  <InputField
                    label={t("Tax ID Number (Optional)")}
                    type="text"
                    name="tax_id_number"
                    value={form.values.tax_id_number}
                    onChange={form.handleChange}
                    error={store.errors.values.tax_id_number}
                    placeholder="e.g., TAX-123456789"
                  />
                </div>
              </>
            )}

            {/* Step 2: Business Address */}
            {step === 2 && (
              <>
                <h3 className="font-semibold text-2xl">
                  {t("Your business address")}
                </h3>
                <div className="space-y-4 gap-4 mt-6">
                  <SelectField
                    label={t("Country")}
                    name="country"
                    value={form.values.country}
                    onChange={form.handleChange}
                    options={Country.getAllCountries().map((item) => ({
                      label: item.name,
                      value: item.isoCode,
                    }))}
                  />
                  <SelectField
                    label={t("State/Region/Province")}
                    name="region"
                    value={form.values.region}
                    onChange={form.handleChange}
                    options={
                      form.values.country
                        ? State.getStatesOfCountry(form.values.country).map(
                            (item) => ({
                              label: item.name,
                              value: item.isoCode,
                            })
                          )
                        : []
                    }
                  />
                  <InputField
                    label={t("City")}
                    name="city"
                    value={form.values.city}
                    onChange={form.handleChange}
                  />
                  <InputField
                    label={t("Street Address")}
                    name="address"
                    value={form.values.address}
                    onChange={form.handleChange}
                    error={store.errors.values.address}
                  />
                </div>
              </>
            )}

            {/* Step 3: Official Documents */}
            {step === 3 && (
              <>
                <h3 className="font-semibold mt-8 text-2xl">
                  {t("Your business official documents")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div className="space-y-3">
                    <label className="text-sm block font-medium leading-none text-gray-700">
                      {t("National ID Card (Front)")}
                    </label>
                    <ImageField
                      name="nationalIdCardFront"
                      image={form.getImageValue("nationalIdCardFront")}
                      onImageChange={form.handleImageChange(
                        "nationalIdCardFront"
                      )}
                      error={store.errors.values.nationalIdCardFront}
                      id="nationalIdCardFront"
                      className="h-40 border-dashed"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm block font-medium leading-none text-gray-700">
                      {t("National ID Card (Back)")}
                    </label>
                    <ImageField
                      name="nationalIdCardBack"
                      image={form.getImageValue("nationalIdCardBack")}
                      onImageChange={form.handleImageChange(
                        "nationalIdCardBack"
                      )}
                      error={store.errors.values.nationalIdCardBack}
                      id="nationalIdCardBack"
                      className="h-40 border-dashed"
                    />
                  </div>
                  <div className="space-y-3 col-span-2">
                    <label className="text-sm block font-medium leading-none text-gray-700">
                      {t("Taxation Documents")}
                    </label>
                    <FileField
                      name="taxation_document"
                      file={form.getFileValue("taxation_document")}
                      error={store.errors.values.taxation_document}
                      onFileChange={form.handleFileChange("taxation_document")}
                      id="taxation_document"
                      className="border-dashed"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between flex-wrap space-y-4 mt-8">
              {step === 3 && (
                <Button
                  onClick={submit}
                  loading={store.loading.status.create}
                  className="w-full py-2"
                >
                  {t("Submit")}
                </Button>
              )}
              {step < 3 && (
                <Button onClick={nextStep} className="ml-auto w-full py-2">
                  {t("Continue")}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              )}
              {step > 1 && (
                <Button
                  color="outline"
                  className="w-full border-none hover:bg-primary-100 transition-all duration-300"
                  onClick={prevStep}
                >
                  <ChevronLeft />
                  {t("Back")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </SetupLayout>
  );
}
